import { createFileRoute, Outlet } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { useCurrentUser } from '~/hooks/useCurrentUser';

/**
 * The *Follow-Up Tutorials* shell — the archive and the single post.
 *
 * This is the only one of the five WBS content types with a role gate on it.
 * Legacy's is an ALLOWLIST of exactly three BuddyPress member types:
 * `themes/wbs/modules/follow-up/class-follow-up-tutorials.php:88` calls
 * `wbs_user_is('follow-ups,admin')`, which
 * `plugins/wbs-core/modules/Core/functions.php:207-209` expands to
 * {follow-up-partner, follow-up-coordinator} and `:197-200` to {administrator}.
 * `WBS_Profile_Types.php:14-16 matchTypes` returns false for a falsy member
 * type, so a user with NO member type is DENIED — this is not a
 * "not-a-student" blocklist and must not be rewritten as one. That last case
 * is LEGACY archaeology and nothing here can reproduce it: `me.profile_type`
 * is non-nullable server-side (`users.profile_type` is the polymorphic
 * discriminator, `is_nullable = NO`), so the typeless user does not exist in
 * this stack and the check below is an allowlist because legacy's is, not
 * because a typeless caller could reach it.
 *
 * Measured live with a cookie jar: study helper 1807 -> 404 on both URLs,
 * web student 1107 -> 404, anonymous -> 404; admin 1808, coordinator 993 and
 * partner 994 -> 200 on both.
 *
 * DIV-1 — v3 refuses with 403 `forbidden` (or 401 `invalid_auth_token` when
 * anonymous) where legacy serves a real HTTP 404. The page-not-found
 * *appearance* is produced here, client-side, by this shell. That is the shape
 * `qa/web/follow-up-network-access.spec.ts` already pins for the identical
 * audience.
 *
 * Defence in depth over the same set the two endpoints enforce, never the only
 * gate.
 */
function FollowUpTutorialsLayout(): ReactElement | null {
  const { me, isPending } = useCurrentUser();

  // `me.profile_type`, not `useCurrentUser().isStudyHelper` — that helper folds
  // STUDY_HELPER in with the three admitted types
  // (`hooks/useCurrentUser.ts:14-19`) and would admit the study helpers both
  // endpoints refuse.
  const allowed =
    me?.profile_type === 'FOLLOWUP_COORDINATOR' ||
    me?.profile_type === 'FOLLOWUP_PARTNER' ||
    // `admin_bypass` defaults to true on the server's
    // FOLLOW_UP_TUTORIALS_REQUIREMENT (legacy's literal `'admin'` in
    // `wbs_user_is`), so an admin gets a 200 from both operations and a gate
    // that omitted them would paint NotFoundScreen over a working response.
    // There is deliberately no admin NAV item — see `navigationConfig.ts`,
    // DIV-11.
    me?.profile_type === 'ADMIN';

  // `/users/me` is pending on first paint, and a bare `!allowed` bounces every
  // caller, coordinators included.
  if (isPending) return null;
  if (!allowed) return <NotFoundScreen />;

  return <Outlet />;
}

export const Route = createFileRoute('/_app/follow-up-tutorials')({
  component: FollowUpTutorialsLayout,
});
